import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { 
  Plus, 
  AlertCircle, 
  Calendar as CalendarIcon, 
  DollarSign, 
  Building2, 
  Mail, 
  Phone,
  FileText,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  Bell,
  Search,
  Download,
  BarChart3,
  ShieldCheck,
  Lock,
  Unlock,
  CreditCard,
  Edit2,
  Trash2
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { toast } from 'sonner';
import { ScrollArea } from '../ui/scroll-area';
import { LICENSE_MANAGEMENT_ADDONS, formatPrice } from '../../utils/pricingConfig';

/**
 * The add-on is a paid PathKit product: a system admin turns it on for the firm
 * (System Admin › Subscriptions). This screen asks for it instead of granting it.
 */
export function addonRequestHref(tier: string, firmId: string): string {
  const subject = encodeURIComponent(`License management add-on (${tier})`);
  const body = encodeURIComponent(`Please turn on license management (${tier}) for our firm (${firmId}).`);
  return `mailto:hello@pathkit.pro?subject=${subject}&body=${body}`;
}

interface License {
  id: string;
  firmId: string;
  clientCompanyId?: string;
  clientCompanyName: string;
  productName: string;
  vendor: string;
  licenseType: string;
  quantity: number;
  startDate?: string;
  expirationDate: string;
  annualValue: number;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  notes: string;
  status: string;
  renewalProbability?: number;
  pipelineStage?: string;
  createdAt: string;
  updatedAt: string;
}

interface Subscription {
  hasAccess: boolean;
  tier: 'none' | 'basic' | 'pro' | 'enterprise';
  addonId: string | null;
  maxLicenses: number;
  currentCount: number;
  addedAt: string | null;
}

interface LicenseManagementProProps {
  accessToken: string;
  firmId: string;
  currentUser: any;
  /** Called with the portfolio each time it loads (Firm › Licenses feeds the dashboard above from it). */
  onLicensesChanged?: (licenses: License[]) => void;
}

export function LicenseManagementPro({ accessToken, firmId, currentUser, onLicensesChanged }: LicenseManagementProProps) {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVendor, setFilterVendor] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    clientCompanyName: '',
    productName: '',
    vendor: '',
    licenseType: 'subscription',
    quantity: 1,
    startDate: '',
    expirationDate: '',
    annualValue: 0,
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSubscription(),
        loadLicenses(),
        loadAnalytics()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load license data');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/license/subscription`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load subscription');
      }

      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadLicenses = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/license/licenses`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.requiresAddon) {
          // Not subscribed yet
          return;
        }
        throw new Error('Failed to load licenses');
      }

      const data = await response.json();
      setLicenses(data.licenses || []);
      onLicensesChanged?.(data.licenses || []);
    } catch (error) {
      console.error('Error loading licenses:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/license/analytics`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.requiresAddon) {
          return;
        }
        throw new Error('Failed to load analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleAddLicense = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/license/licenses`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.requiresUpgrade) {
          toast.error(errorData.error);
          return;
        }
        throw new Error(errorData.error || 'Failed to add license');
      }

      toast.success('License added');
      setShowAddDialog(false);
      resetForm();
      await loadData();
    } catch (error: any) {
      console.error('Error adding license:', error);
      toast.error(error.message || 'Failed to add license');
    }
  };

  const handleUpdateLicense = async () => {
    if (!selectedLicense) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/license/licenses/${selectedLicense.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update license');
      }

      toast.success('License updated');
      setShowEditDialog(false);
      setSelectedLicense(null);
      await loadData();
    } catch (error: any) {
      console.error('Error updating license:', error);
      toast.error(error.message || 'Failed to update license');
    }
  };

  const handleDeleteLicense = async (licenseId: string) => {
    if (!confirm('Are you sure you want to delete this license?')) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/license/licenses/${licenseId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete license');
      }

      toast.success('License deleted');
      await loadData();
    } catch (error: any) {
      console.error('Error deleting license:', error);
      toast.error(error.message || 'Failed to delete license');
    }
  };

  const handleUpdatePipeline = async (licenseId: string, pipelineStage: string, renewalProbability?: number) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/license/licenses/${licenseId}/pipeline`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pipelineStage, renewalProbability }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.requiresUpgrade) {
          toast.error('Pipeline feature requires Pro or Enterprise plan');
          return;
        }
        throw new Error(errorData.error || 'Failed to update pipeline');
      }

      toast.success('Pipeline updated');
      await loadLicenses();
    } catch (error: any) {
      console.error('Error updating pipeline:', error);
      toast.error(error.message || 'Failed to update pipeline');
    }
  };

  const resetForm = () => {
    setFormData({
      clientCompanyName: '',
      productName: '',
      vendor: '',
      licenseType: 'subscription',
      quantity: 1,
      startDate: '',
      expirationDate: '',
      annualValue: 0,
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      notes: '',
    });
  };

  const openEditDialog = (license: License) => {
    setSelectedLicense(license);
    setFormData({
      clientCompanyName: license.clientCompanyName,
      productName: license.productName,
      vendor: license.vendor,
      licenseType: license.licenseType,
      quantity: license.quantity,
      startDate: license.startDate || '',
      expirationDate: license.expirationDate,
      annualValue: license.annualValue,
      contactName: license.contactName,
      contactEmail: license.contactEmail,
      contactPhone: license.contactPhone,
      notes: license.notes,
    });
    setShowEditDialog(true);
  };

  const getDaysUntilExpiry = (expirationDate: string) => {
    return differenceInDays(new Date(expirationDate), new Date());
  };

  const getExpiryBadge = (expirationDate: string) => {
    const days = getDaysUntilExpiry(expirationDate);
    
    if (days < 0) {
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Expired</Badge>;
    } else if (days <= 30) {
      return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" />{days}d</Badge>;
    } else if (days <= 60) {
      return <Badge variant="outline" className="gap-1 border-orange-500 text-orange-600"><Clock className="h-3 w-3" />{days}d</Badge>;
    } else if (days <= 90) {
      return <Badge variant="outline" className="gap-1 border-yellow-500 text-yellow-600"><Clock className="h-3 w-3" />{days}d</Badge>;
    }
    return <Badge variant="outline" className="gap-1"><CheckCircle2 className="h-3 w-3" />{days}d</Badge>;
  };

  const filteredLicenses = licenses.filter(lic => {
    const matchesSearch = !searchTerm || 
      lic.clientCompanyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lic.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lic.contactName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesVendor = filterVendor === 'all' || lic.vendor === filterVendor;
    
    return matchesSearch && matchesVendor;
  });

  // Show upgrade prompt if not subscribed
  if (!subscription?.hasAccess) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              License Management
            </CardTitle>
            <CardDescription>
              Premium add-on for tracking client licenses and managing renewals
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertTitle>Premium Feature</AlertTitle>
              <AlertDescription>
                License management is a premium add-on that PathKit turns on for your firm. Choose a tier below to ask PathKit to turn it on.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              {/* Basic Plan */}
              <Card>
                <CardHeader>
                  <CardTitle>Basic</CardTitle>
                  <div className="text-3xl mt-2">{formatPrice(LICENSE_MANAGEMENT_ADDONS.license_basic.price)}<span className="text-sm">/month</span></div>
                  <CardDescription>For small teams</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Track up to 50 licenses
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Expiration alerts
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Basic reporting
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Contact management
                    </li>
                  </ul>
                  <Button 
                    className="w-full" 
                    asChild
                  >
                    <a href={addonRequestHref('Basic', firmId)}>Ask PathKit to turn on Basic</a>
                  </Button>
                </CardContent>
              </Card>

              {/* Pro Plan */}
              <Card className="border-primary">
                <CardHeader>
                  <Badge className="w-fit mb-2">Most Popular</Badge>
                  <CardTitle>Pro</CardTitle>
                  <div className="text-3xl mt-2">{formatPrice(LICENSE_MANAGEMENT_ADDONS.license_pro.price)}<span className="text-sm">/month</span></div>
                  <CardDescription>For growing businesses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Track up to 200 licenses
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Renewal pipeline management
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Probability tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Advanced analytics
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Revenue forecasting
                    </li>
                  </ul>
                  <Button 
                    className="w-full" 
                    asChild
                  >
                    <a href={addonRequestHref('Pro', firmId)}>Ask PathKit to turn on Pro</a>
                  </Button>
                </CardContent>
              </Card>

              {/* Enterprise Plan */}
              <Card>
                <CardHeader>
                  <CardTitle>Enterprise</CardTitle>
                  <div className="text-3xl mt-2">{formatPrice(LICENSE_MANAGEMENT_ADDONS.license_enterprise.price)}<span className="text-sm">/month</span></div>
                  <CardDescription>For large organizations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Unlimited licenses
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Full procurement workflow
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Compliance tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Custom integrations
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Priority support
                    </li>
                  </ul>
                  <Button 
                    className="w-full" 
                    asChild
                  >
                    <a href={addonRequestHref('Enterprise', firmId)}>Ask PathKit to turn on Enterprise</a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Current Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                License Management
                <Badge variant="outline">{subscription.tier.toUpperCase()} Plan</Badge>
              </CardTitle>
              <CardDescription>
                {subscription.currentCount} of {subscription.maxLicenses === Infinity ? '∞' : subscription.maxLicenses} licenses used
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button onClick={() => setShowAddDialog(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add License
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Analytics Dashboard */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Total Licenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl">{analytics.totalLicenses}</div>
              <p className="text-sm text-muted-foreground">{analytics.activeLicenses} active</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Expiring in 30 Days</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-red-600">{analytics.expiring.next30Days}</div>
              <p className="text-sm text-muted-foreground">Needs attention</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Annual Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl">${analytics.totalAnnualValue.toLocaleString()}</div>
              <p className="text-sm text-muted-foreground">Total portfolio</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Expiring (60-90d)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-orange-600">{analytics.expiring.next60Days + analytics.expiring.next90Days}</div>
              <p className="text-sm text-muted-foreground">Plan ahead</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">All Licenses</TabsTrigger>
              <TabsTrigger value="pipeline" disabled={subscription.tier === 'basic'}>
                Renewal Pipeline {subscription.tier === 'basic' && <Lock className="h-3 w-3 ml-1" />}
              </TabsTrigger>
              <TabsTrigger value="analytics" disabled={subscription.tier === 'basic'}>
                Analytics {subscription.tier === 'basic' && <Lock className="h-3 w-3 ml-1" />}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              {/* Filters */}
              <div className="flex gap-2">
                <Input
                  placeholder="Search licenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
                <Select value={filterVendor} onValueChange={setFilterVendor}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Vendors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    <SelectItem value="Atlassian">Atlassian</SelectItem>
                    <SelectItem value="ServiceNow">ServiceNow</SelectItem>
                    <SelectItem value="Ivanti">Ivanti</SelectItem>
                    <SelectItem value="BMC">BMC Helix</SelectItem>
                    <SelectItem value="Freshservice">Freshservice</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Licenses Table */}
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Annual Value</TableHead>
                      <TableHead>Expiration</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLicenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                          No licenses found. Click "Add License" to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLicenses.map((license) => (
                        <TableRow key={license.id}>
                          <TableCell>
                            <div>
                              <div>{license.clientCompanyName}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div>{license.productName}</div>
                              <div className="text-sm text-muted-foreground">{license.licenseType}</div>
                            </div>
                          </TableCell>
                          <TableCell>{license.vendor}</TableCell>
                          <TableCell>{license.quantity}</TableCell>
                          <TableCell>${license.annualValue.toLocaleString()}</TableCell>
                          <TableCell>
                            <div>
                              <div className="text-sm">{format(new Date(license.expirationDate), 'MMM d, yyyy')}</div>
                              {getExpiryBadge(license.expirationDate)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{license.contactName}</div>
                              <div className="text-muted-foreground">{license.contactEmail}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditDialog(license)}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleDeleteLicense(license.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="pipeline">
              {subscription.tier === 'pro' || subscription.tier === 'enterprise' ? (
                <div className="space-y-4">
                  <CardDescription>Track renewal conversations and forecast revenue</CardDescription>
                  
                  {analytics?.pipeline && (
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                      {Object.entries(analytics.pipeline).map(([stage, count]) => (
                        <Card key={stage}>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm capitalize">{stage.replace('_', ' ')}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-3xl">{count as number}</div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Alert>
                  <Lock className="h-4 w-4" />
                  <AlertTitle>Pro Feature</AlertTitle>
                  <AlertDescription>
                    Renewal pipeline management requires the Pro or Enterprise plan.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="analytics">
              {subscription.tier === 'pro' || subscription.tier === 'enterprise' ? (
                <div className="space-y-4">
                  <CardDescription>Insights and trends across your license portfolio</CardDescription>
                  <p className="text-muted-foreground">
                    Advanced analytics and forecasting tools
                  </p>
                </div>
              ) : (
                <Alert>
                  <Lock className="h-4 w-4" />
                  <AlertTitle>Pro Feature</AlertTitle>
                  <AlertDescription>
                    Advanced analytics requires the Pro or Enterprise plan.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add License Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New License</DialogTitle>
            <DialogDescription>
              Track a new client license in your portfolio
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Company Name *</Label>
                <Input
                  value={formData.clientCompanyName}
                  onChange={(e) => setFormData({ ...formData, clientCompanyName: e.target.value })}
                  placeholder="Acme Corp"
                />
              </div>
              <div className="space-y-2">
                <Label>Product Name *</Label>
                <Input
                  value={formData.productName}
                  onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                  placeholder="Jira Software"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor *</Label>
                <Select value={formData.vendor} onValueChange={(value) => setFormData({ ...formData, vendor: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Atlassian">Atlassian</SelectItem>
                    <SelectItem value="ServiceNow">ServiceNow</SelectItem>
                    <SelectItem value="Ivanti">Ivanti</SelectItem>
                    <SelectItem value="BMC">BMC Helix</SelectItem>
                    <SelectItem value="Freshservice">Freshservice</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>License Type</Label>
                <Select value={formData.licenseType} onValueChange={(value) => setFormData({ ...formData, licenseType: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subscription">Subscription</SelectItem>
                    <SelectItem value="perpetual">Perpetual</SelectItem>
                    <SelectItem value="cloud">Cloud</SelectItem>
                    <SelectItem value="on-premise">On-Premise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiration Date *</Label>
                <Input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Annual Value</Label>
              <Input
                type="number"
                value={formData.annualValue}
                onChange={(e) => setFormData({ ...formData, annualValue: parseFloat(e.target.value) || 0 })}
                placeholder="25000"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  placeholder="john@acme.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  placeholder="+1-555-0123"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional information..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddLicense}>
              Add License
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit License Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit License</DialogTitle>
            <DialogDescription>
              Update license information
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Company Name *</Label>
                <Input
                  value={formData.clientCompanyName}
                  onChange={(e) => setFormData({ ...formData, clientCompanyName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Product Name *</Label>
                <Input
                  value={formData.productName}
                  onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor *</Label>
                <Select value={formData.vendor} onValueChange={(value) => setFormData({ ...formData, vendor: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Atlassian">Atlassian</SelectItem>
                    <SelectItem value="ServiceNow">ServiceNow</SelectItem>
                    <SelectItem value="Ivanti">Ivanti</SelectItem>
                    <SelectItem value="BMC">BMC Helix</SelectItem>
                    <SelectItem value="Freshservice">Freshservice</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>License Type</Label>
                <Select value={formData.licenseType} onValueChange={(value) => setFormData({ ...formData, licenseType: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subscription">Subscription</SelectItem>
                    <SelectItem value="perpetual">Perpetual</SelectItem>
                    <SelectItem value="cloud">Cloud</SelectItem>
                    <SelectItem value="on-premise">On-Premise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiration Date *</Label>
                <Input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Annual Value</Label>
              <Input
                type="number"
                value={formData.annualValue}
                onChange={(e) => setFormData({ ...formData, annualValue: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateLicense}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
